import React from 'react';
import I18n from 'i18n-js';

import { PencilIcon } from '@reverbdotcom/cadence/icons/react';
import { RCButton, RCGuidePopover, RCTextWithIcon, RCToast } from '@reverbdotcom/cadence/components';
import QuickPriceEditModal from './quick_price_edit_modal';
import { useCreateUserAction, withCreateUserActionMutation, CoreUserActionsCreateMutationFn } from '@reverbdotcom/commons/src/user_actions/create_action_mutation';
import { connect } from '@reverbdotcom/commons/src/connect';
import { core_apimessages_UserActionName, core_apimessages_Money } from '@reverbdotcom/commons/src/rql';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { PriceDisplay } from '@reverbdotcom/commons';

const LIVE = 'live';
const DRAFT = 'draft';
const ENDED = 'ended';

export const VALID_LISTING_STATES_FOR_QUICK_PRICE_EDIT = [
  LIVE,
  DRAFT,
  ENDED,
];
interface IExternalProps {
  listingId: string | number;
  defaultSellerPrice: core_apimessages_Money;
  userUuid: string;
  showQuickPriceEditPopover?: boolean;
  isEditable?: boolean;
  onSuccessCallback?: () => any;
}
interface IMutationProps {
  createUserAction: CoreUserActionsCreateMutationFn;
}

type IProps = IExternalProps & IMutationProps;

export const COMPONENT_NAME = 'QuickPriceEditButton';

export function QuickPriceEditButton({
  createUserAction,
  defaultSellerPrice,
  listingId,
  showQuickPriceEditPopover,
  userUuid,
  isEditable = true,
  onSuccessCallback,
}: IProps) {
  const [sellerPrice, updateSellerPrice] = React.useState(defaultSellerPrice);
  const [showToast, setShowToast] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [showPopover, setShowPopover] = React.useState(showQuickPriceEditPopover);

  const createUserActionState = useCreateUserAction(
    core_apimessages_UserActionName.QUICK_PRICE_EDIT_CALLOUT,
    {
      uuid: userUuid,
    },
    {
      listingId: `${listingId}`,
    },
    createUserAction,
  );

  const searchParams = new URLSearchParams(window.location.search);

  React.useEffect(() => {
    trackEvent({
      eventName: MParticleEventName.ComponentView,
      componentName: COMPONENT_NAME,
      listingId,
    });
  }, []);

  React.useEffect(() => {
    return () => {
      setShowPopover(false);
    };
  }, [searchParams]);

  return (
    <div>
      <RCGuidePopover
        title={I18n.t('discovery.listingsManagement.quickPriceEdit.popover.title')}
        content={I18n.t('discovery.listingsManagement.quickPriceEdit.popover.content')}
        isOpen={isEditable && showPopover}
        onDismiss={() => {
          setShowPopover(false);
          createUserActionState.createUserAction();
        }}
        preventAutoFocus
        anchor={<div className="size-100 weight-bold mb-1">
          {isEditable ? (
            <RCButton
              variant="tertiary"
              size="mini"
              onClick={() => setIsModalOpen(true)}
            >
              <RCTextWithIcon
                svgComponent={PencilIcon}
                placement="right"
              >
                {sellerPrice.amountCents === 0
                  ? I18n.t(
                    'discovery.listingsManagement.quickPriceEdit.button.addPrice',
                  )
                  : sellerPrice.display}
              </RCTextWithIcon>
            </RCButton>
          ) : (
            <PriceDisplay
              amountCents={sellerPrice.amountCents}
              display={sellerPrice.display}
            />
          )}
        </div>}
      />

      <QuickPriceEditModal
        isOpen={isModalOpen}
        listingId={listingId}
        requestClose={() => setIsModalOpen(false)}
        onSuccessCallback={async (updatedPrice) => {
          updateSellerPrice(updatedPrice);
          setIsModalOpen(false);
          setShowToast(true);
          if (onSuccessCallback) {
            await onSuccessCallback();
          }
        }}
        sellerPrice={sellerPrice}
      />

      <RCToast
        isOpen={showToast}
        onClose={() => setShowToast(false)}
        text={I18n.t('discovery.listingsManagement.toast.message.success')}
        theme="success"
        delayInterval="short"
      />
    </div>
  );
}

export default connect<IExternalProps>([
  withCreateUserActionMutation,
])(QuickPriceEditButton);
